/**
 * Frontend Sentry wrapper — silent no-op when VITE_SENTRY_DSN is unset.
 *
 * Mirrors the backend's `services/sentry.py` so the policy is consistent:
 *  - No DSN → every call is a no-op, no network requests, no console noise.
 *  - With DSN → forward to @sentry/react.
 *
 * PII policy (MEMORY §6): never put chart payloads, names, locations,
 * or chart_ids in `extra`. Pass structured context (route, status,
 * error name) only.
 */

import * as Sentry from "@sentry/react";

let initialized = false;

export function init(): void {
  if (initialized) return;
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
  if (!dsn) {
    // Silent no-op in dev/CI/local.
    return;
  }
  const environment = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "production";
  Sentry.init({
    dsn,
    environment,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    // React Router v6 integration — captures route transitions.
    integrations: [Sentry.browserTracingIntegration()],
  });
  initialized = true;
}

export function captureException(
  error: unknown,
  extra?: Record<string, unknown>,
): string | undefined {
  if (!initialized) return undefined;
  return Sentry.captureException(error, { extra });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = "info"): void {
  if (!initialized) return;
  Sentry.captureMessage(message, level);
}

export function isEnabled(): boolean {
  return initialized;
}